import { HttpClient, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FeatureFlagService, StorageManagerService } from '@core/services';
import {
  addRandomBillingAddressesToGarages,
  addRandomProductsToGarages,
  exampleArchivedBillingInvoices,
  exampleBillingInvoices,
  exampleContractRequests,
  exampleGarageContracts,
  exampleGarages,
  exampleLicensePlates,
  exampleOpenCosts,
  examplePayPerUseLicensePlates,
  examplePaymentMethod,
  exampleProducts,
  exampleProviderSettings,
  exampleUser,
  exampleVehicles,
  generateExampleUnpaidParkingSessions,
  generateRandomOpenParkingSessions,
} from '@core/utils';
import { environment } from '@environment';
import {
  ContractTemplate,
  CoreData,
  GarageContractCreate,
  LicensePlate,
  PayPerUseLicensePlate,
  SignupParkingSession,
  SignupSearchStatusResponse,
  SignupSummaryResponse,
  Vehicle,
  VerifySignupJWTResponse,
} from '@shared/models';
import { Observable, delay, of, switchMap, throwError } from 'rxjs';

type MockApiEndpoint = {
  url_start: string;
  url_end?: string;
  method: string;
  response: (req: HttpRequest<any>) => Observable<HttpResponse<any>>;
};

@Injectable()
export class MockHttpInterceptor implements HttpInterceptor {
  baseUrl =
    environment.backend_url && environment.backend_url.trim() !== ''
      ? environment.backend_url
      : `${window.location.protocol}//${window.location.host}`;
  //prettier-ignore
  private mockApiEndpoints: MockApiEndpoint[] = [
    //AUTH
    { url_start: '/api/user/auth/app/v1/auth/code/request/', method: 'POST', response: this.mockLoginEmailResponse.bind(this) },
    { url_start: '/api/user/auth/app/v1/auth/code/confirm', method: 'POST', response: this.mockLoginTokenResponse.bind(this) },
    { url_start: '/api/user/auth/browser/confirm_login', method: 'POST', response: this.mockConfirmLoginCookieResponse.bind(this) },
    { url_start: '/api/user/auth/app/v1/auth/signup/', method: 'POST', response: this.mockRegisterVerifyResponse.bind(this) },
    { url_start: '/api/user/v2/auth/register/email', method: 'POST', response: this.mockRegisterEmailResponse.bind(this) },
    { url_start: '/api/user/v2/auth/register/activate', method: 'POST', response: this.mockActivateAccountResponse.bind(this) },
    { url_start: '/api/user/v2/user/welcome_email_from_token/', method: 'POST', response: this.mockWelcomeEmailFromTokenResponse.bind(this) },
    { url_start: '/api/user/auth/app/v1/auth/session', method: 'DELETE', response: this.mockLogoutResponse.bind(this) },
    { url_start: '/api/user/auth/app/v1/auth/email/verify', method: 'POST', response: this.mockEmailVerifyResponse.bind(this) },
    //RESOLVERS
    { url_start: '/api/backoffice/v2/customer/license_plate/', method: 'GET', response: this.mockVehiclesResponse.bind(this) },
    { url_start: '/api/backoffice/v2/customer_portal/dashboard_data/', method: 'GET', response: this.mockDashboardResponse.bind(this), },
    { url_start: '/api/invoice/v2/customer_portal/billing_data/', method: 'GET', response: this.mockBillingResponse.bind(this) },
    { url_start: '/api/backoffice/v2/customer_portal/contract/', url_end: '/create/', method: 'GET', response: this.mockContractCreateResponse.bind(this) },
    { url_start: '/api/backoffice/v2/customer_portal/contract/', url_end: '/update/', method: 'GET', response: this.mockContractEditResponse.bind(this) },
    { url_start: '/api/backoffice/v2/customer_portal/core/', method: 'GET', response: this.mockCoreDataResponse.bind(this) },
    { url_start: '/api/backoffice/v2/customer_portal/onboarding/', method: 'GET', response: this.mockOnboardingResponse.bind(this) },
    { url_start: '/api/backoffice/v2/customer_portal/products/', method: 'GET', response: this.mockProductsResponse.bind(this) },
    { url_start: '/api/backoffice/v2/customer/self/', method: 'GET', response: this.mockUserResponse.bind(this) },
    { url_start: '/api/backoffice/v2/customer_portal/provider_data/', method: 'GET', response: this.mockProviderSettingsResponse.bind(this) },
    { url_start: '/api/user/v2/auth/register/summary/', method: 'GET', response: this.mockSignupSummaryResponse.bind(this) },
    //ACTIONS
    { url_start: '/api/backoffice/v2/customer_portal/door_opener_qr_code/', method: 'GET', response: this.mockQrCodeResponse.bind(this) },
    { url_start: '/api/backoffice/v2/customer_portal/document/', method: 'PUT', response: this.mockDocumentUploadResponse.bind(this) },
    { url_start: '/api/backoffice/v2/customer/license_plate/', url_end: '/description/', method: 'PUT', response: this.mockVehicleDescriptionPutResponse.bind(this) },
    { url_start: '/api/backoffice/v2/customer/license_plate/', method: 'DELETE', response: this.mockVehicleDeleteResponse.bind(this) },
    { url_start: '/api/backoffice/v2/customer_portal/pay_per_use/get_or_create/', method: 'POST', response: this.mockTogglePayPerUseResponse.bind(this) },
    { url_start: '/api/backoffice/v2/customer_portal/pay_per_use/delete_if_exists/', method: 'PUT', response: this.mockTogglePayPerUseResponse.bind(this) },
    { url_start: '/api/backoffice/v2/customer_portal/pay_per_use/withdraw/', method: 'POST', response: this.mockPayPerUseWithdrawResponse.bind(this) },
    { url_start: '/api/backoffice/v2/customer_portal/pay_per_use/', method: 'POST', response: this.mockAddPayPerUseResponse.bind(this) },
    { url_start: '/api/backoffice/v2/customer_portal/contract/', url_end: '/contract/', method: 'POST', response: this.mockContractPostResponse.bind(this) },
    { url_start: '/api/backoffice/v2/customer_portal/contract/', url_end: '/license_plates/', method: 'PUT', response: this.mockContractPutResponse.bind(this) },
    { url_start: '/api/backoffice/v2/garage_contract/preview/', method: 'GET', response: this.mockContractGetInfoResponse.bind(this) },
    { url_start: '/api/backoffice/v2/garage_contract/', url_end: '/terminate/', method: 'PUT', response: this.mockContractTerminateResponse.bind(this) },
    { url_start: '/api/backoffice/v2/garage_contract/', url_end: '/withdraw/', method: 'PUT', response: this.mockContractWithdrawResponse.bind(this) },
    { url_start: '/api/user/v2/auth/register/search/', method: 'GET', response: this.mockSignupSearchStatusResponse.bind(this) },
    { url_start: '/api/backoffice/v2/customer/update/', method: 'PUT', response: this.mockUserPutResponse.bind(this) },
    //ADYEN
    { url_start: '/api/invoice/v2/payment_method/', url_end: '/submit_authentication_details/', method: 'POST', response: this.mockAdditionalDetailsResponse.bind(this) },
    { url_start: '/api/invoice/v2/payment_method/', url_end: '/available_payment_methods/', method: 'GET', response: this.mockAvailablePaymentMethodsResponse.bind(this) },
    { url_start: '/api/invoice/v2/payment_method/', url_end: '/payment_method/', method: 'POST', response: this.mockInitiatePaymentSessionResponse.bind(this) },
  ];

  constructor(
    private http: HttpClient,
    private storageManagerService: StorageManagerService,
    private featureFlagService: FeatureFlagService,
  ) {
    if (this.featureFlagService.getFeatureFlag('dev_enable_mock_calls')) {
      console.warn('MockHttpInterceptor is enabled, backend_url:', this.baseUrl);
    }
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (
      (this.featureFlagService.getFeatureFlag('dev_enable_mock_calls') ||
        (this.storageManagerService.load('token') === 'demotoken' && this.featureFlagService.getFeatureFlag('dev_enable_demo_login'))) &&
      req.url.startsWith('/api')
    ) {
      const endpoint = this.mockApiEndpoints.find((ep) => {
        const startsWith = req.url.startsWith(ep.url_start) && req.method === ep.method;
        const endsWith = ep.url_end ? req.url.endsWith(ep.url_end) : true;
        return startsWith && endsWith;
      });
      if (endpoint) {
        return endpoint.response(req);
      }
    }
    return next.handle(req);
  }

  loginResponse () {
    return {
      "status": 401,
      "data": {
        "flows": [
          {"id": "login", "is_pending": false}, {"id": "signup"}, {"id": "provider_token", "providers": ["google"]}]
        },
      "meta": {"is_authenticated": false} as { "is_authenticated": boolean; "session_token"?: string }
    }
  }

  mockAvailablePaymentMethodsResponse() {
    return this.handleRealResponse(
      'get',
      environment.name === 'development'
        ? `${environment.dev_adyen_backend_url}/available_payment_methods`
        : `${this.baseUrl}/api/invoice/v2/payment_method/available_payment_methods/`,
    );
  }

  mockInitiatePaymentSessionResponse(req: HttpRequest<any>) {
    return this.handleRealResponse(
      'post',
      environment.name === 'development'
        ? `${environment.dev_adyen_backend_url}/payment_method`
        : `${this.baseUrl}/api/invoice/v2/payment_method/`,
      req.body,
    );
  }

  mockAdditionalDetailsResponse(req: HttpRequest<any>) {
    return this.handleRealResponse(
      'post',
      environment.name === 'development'
        ? `${environment.dev_adyen_backend_url}/submit_authentication_details`
        : `${this.baseUrl}/api/invoice/v2/payment_method/submit_authentication_details/`,
      req.body,
    );
  }

  mockRegisterVerifyResponse(req: HttpRequest<any>) {
    if (environment.name === 'development') {
      // const mockSignupResponse: VerifySignupJWTResponse = {
      //   key: 'demotoken',
      //   onboarding_parking_session: {
      //     price: {
      //       amount: '3.20',
      //       currency: 'EUR',
      //     },
      //     entry_time: '2024-08-01T11:29:15',
      //     duration_in_seconds: 2958,
      //     license_plate: {
      //       license_plate: 'G:WIPA100',
      //       license_plate_country: 'D',
      //     },
      //     is_cleared: true,
      //   },
      // };
      if (req?.body?.token === "ErrorToken") {
        return this.handleMockError(401, this.loginResponse());
      }
      const mockSignupResponse: VerifySignupJWTResponse = {
        data: {
          user: {
            id: 'userid',
            display: 'userdisplay',
            has_usable_password: false,
            email: 'demouser@demo.at',
            username: 'demousername',
          },
          methods: [],
        },
        meta: {
          is_authenticated: true,
          session_token: 'demotoken',
        },
      };
      return this.handleMockResponse(mockSignupResponse, req, 250);
    }
    return this.handleRealResponse('post', this.baseUrl + req.url, req.body);
  }

  mockDocumentUploadResponse(req: HttpRequest<any>) {
    if (environment.name === 'development') {
      if (req.url.endsWith('error.pdf/')) {
        return this.handleMockError(400, {"detail": {"file": ['File upload failed due to invalid file format.']}}, 3000);
      }
      return this.handleMockResponse({id: Math.random().toString(36).substring(7), filename: 'correct', size: 123}, req, 3000);
    }
    return this.handleRealResponse('put', this.baseUrl + req.url, req.body);
  }

  mockQrCodeResponse() {
    return this.handleMockError(403);
    // return this.handleRealResponse('get', `${this.baseUrl}/api/backoffice/v2/customer_portal/door_opener_qr_code/`, undefined, {
    //   responseType: 'blob',
    // });
  }

  mockBillingResponse(req: HttpRequest<any>) {
    if (environment.name === 'development' || this.storageManagerService.load('token') === 'demotoken') {
      return this.handleMockResponse(
        {
          payment_method: examplePaymentMethod,
          open_costs: exampleOpenCosts,
          unpaid_parking_sessions: generateExampleUnpaidParkingSessions(100),
          invoices: exampleBillingInvoices,
          archived_invoices: exampleArchivedBillingInvoices,
        },
        req,
      );
    } else {
      return this.handleRealResponse('get', this.baseUrl + req.url);
    }
  }

  mockContractCreateResponse(req: HttpRequest<any>) {
    if (environment.name === 'development' || this.storageManagerService.load('token') === 'demotoken') {
      let garages = exampleGarages;
      addRandomProductsToGarages(garages);

      addRandomBillingAddressesToGarages(garages);
      return this.handleMockResponse(
        {
          garages,
          license_plates: exampleLicensePlates,
        },
        req,
      );
    } else {
      return this.handleRealResponse('get', this.baseUrl + req.url);
    }
  }

  mockDashboardResponse(req: HttpRequest<any>) {
    if (environment.name === 'development' || this.storageManagerService.load('token') === 'demotoken') {
      return this.handleMockResponse(
        {
          payment_method: examplePaymentMethod,
          open_costs: exampleOpenCosts,
          garage_contracts: exampleGarageContracts,
          open_parking_sessions: [...generateRandomOpenParkingSessions(Math.floor(Math.random() * 5))],
          pay_per_use_license_plates: [...examplePayPerUseLicensePlates],
          pay_per_use_garage_count: 5,
          contract_requests: exampleContractRequests,
        },
        req,
      );
    } else {
      return this.handleRealResponse('get', this.baseUrl + req.url);
    }
  }

  mockCoreDataResponse(req: HttpRequest<any>) {
    if (this.storageManagerService.load('token') === 'demotoken' || environment.name === 'development') {
      return this.handleMockResponse<CoreData>(
        {
          customer_id: 'i-am-a-test-id',
          signup_status: {
            has_payment_method: true,
            has_customer_address: true,
            has_enabled_pay_per_use_license_plates: false,
          },
          approval_status: {
            terms_and_conditions: false,
            pay_per_use: false,
            analytics: false,
            marketing: false,
          },
          // ban_status: { active: false, reason: 'other' },
        },
        req,
      );
    } else {
      return this.handleRealResponse('get', this.baseUrl + req.url);
    }
  }

  mockOnboardingResponse(req: HttpRequest<any>) {
    if (this.storageManagerService.load('token') === 'demotoken' || environment.name === 'development') {
      return this.handleMockResponse({ customer: exampleUser, payment_method: null }, req);
    } else {
      return this.handleRealResponse('get', this.baseUrl + req.url);
    }
  }

  mockUserResponse(req: HttpRequest<any>) {
    if (this.storageManagerService.load('token') === 'demotoken' || environment.name === 'development') {
      return this.handleMockResponse(exampleUser, req);
    } else {
      return this.handleRealResponse('get', this.baseUrl + req.url);
    }
  }

  mockProviderSettingsResponse(req: HttpRequest<any>) {
    if (environment.name === 'development') {
      return this.handleMockResponse(exampleProviderSettings, req);
    } else {
      return this.handleRealResponse('get', this.baseUrl + req.url);
    }
  }

  mockSignupSummaryResponse(req: HttpRequest<any>): Observable<HttpResponse<any>> {
    if (environment.name === 'development' || this.storageManagerService.load('token') === 'demotoken') {
      const onboarding_parking_session: SignupParkingSession = {
        price: {
          amount: '3.20',
          currency: 'EUR',
        },
        entry_time: '2024-08-01T11:29:15',
        // exit_time: '2024-08-01T12:29:15',
        duration_in_seconds: 2958,
        license_plate: {
          license_plate: 'G:WIPA100',
          license_plate_country: 'D',
        },
        is_cleared: true,
      };
      const mockSignupSummaryResponse: SignupSummaryResponse = {
        onboarding_parking_session,
      };
      return this.handleMockResponse(mockSignupSummaryResponse, req);
    } else {
      return this.handleRealResponse('get', this.baseUrl + req.url);
    }
  }

  mockSignupSearchStatusResponse(req: HttpRequest<any>): Observable<HttpResponse<any>> {
    // Extract license plate details from the request URL
    const urlParams = new URLSearchParams(req.url.split('?')[1]);
    const licensePlate = urlParams.get('license_plate');

    if (environment.name === 'development') {
      let mockSignupSearchResponse: SignupSearchStatusResponse | null = { signup_search: 'open_session' };

      if (licensePlate?.startsWith('X')) {
        mockSignupSearchResponse = { signup_search: 'cleared_session' };
      }
      if (licensePlate?.startsWith('Y')) {
        mockSignupSearchResponse = { signup_search: 'active_access' };
      }
      if (licensePlate?.startsWith('Z')) {
        mockSignupSearchResponse = null;
      }
      return this.handleMockResponse(mockSignupSearchResponse, req);
    } else {
      return this.handleRealResponse('get', this.baseUrl + req.url);
    }
  }

  mockTogglePayPerUseResponse(req: HttpRequest<any>) {
    if (environment.name === 'development' || this.storageManagerService.load('token') === 'demotoken') {
      const { license_plate, license_plate_country } = req.body;

      const licensePlateIndex = examplePayPerUseLicensePlates.findIndex(
        (lp) => lp.license_plate === license_plate && lp.license_plate_country === license_plate_country,
      );

      if (licensePlateIndex !== -1) {
        examplePayPerUseLicensePlates[licensePlateIndex].enabled = req.url.includes('get_or_create');
        return this.handleMockResponse(examplePayPerUseLicensePlates[licensePlateIndex], req);
      } else {
        return this.mockAddPayPerUseResponse(req);
      }
    } else {
      return this.handleRealResponse(req.method === 'PUT' ? 'put' : 'post', this.baseUrl + req.url, req.body);
    }
  }

  mockAddPayPerUseResponse(req: HttpRequest<any>): Observable<any> {
    if (environment.name === 'development' || this.storageManagerService.load('token') === 'demotoken') {
      let licensePlate: PayPerUseLicensePlate = {
        ...req.body,
        pay_per_use_enabled: true,
      };
      examplePayPerUseLicensePlates.push(licensePlate);
      exampleLicensePlates.push(req.body);

      return this.handleMockResponse(licensePlate, req);
    } else {
      return this.handleRealResponse('post', this.baseUrl + req.url, req.body);
    }
  }

  mockContractPostResponse(req: HttpRequest<any>) {
    if (environment.name === 'development' || this.storageManagerService.load('token') === 'demotoken') {
      let request: GarageContractCreate = req.body;

      const [garageId, productId] = request.contract_template_id.split('-');

      const existingGarage = exampleGarages.find((garage) => garage.id === garageId);
      const existingProduct = exampleProducts.find((product) => product.id === productId);
      let newContractId: string =  Math.random().toString(36).substring(7);

      let isContractRequest =  existingProduct.bookable === 'public' && existingProduct.approval_required;

      if (isContractRequest) {
        const newContractRequest = {
          id: newContractId,
          status: 'pending',
          name: existingProduct.name,
          garage: {
            name: existingGarage.name,
            street: existingGarage.street,
            number: existingGarage.number,
            zip_code: existingGarage.zip_code,
            city: existingGarage.city,
          },
          group_limit: request.group_limit,
          start: request.start,
          license_plates: request.license_plates,
          price: {
            amount: existingProduct.price.amount * request.group_limit,
            currency: 'EUR',
          },
          price_adjustment: existingProduct.price_adjustment,
        };
        exampleContractRequests.unshift(newContractRequest);
      } else {
        let newContract: any = {
          ...request,
          id: newContractId,
          name: existingProduct.name,
          start: request.start,
          end: request.end ?? null,
          withdrawable: true,
          garage: {
            name: existingGarage.name,
            street: existingGarage.street,
            number: existingGarage.number,
            zip_code: existingGarage.zip_code,
            city: existingGarage.city,
          },
          price: {
            amount: existingProduct.price.amount * request.group_limit,
            currency: 'EUR',
          },
          minimum_duration_months: existingProduct.minimum_duration_months,
          notice_period_months: existingProduct.notice_period_months,
        };
        exampleGarageContracts.push(newContract);
      }



      let newContractResponse: { id: string } = {
        id: newContractId
      }
      return this.handleMockResponse(newContractResponse, req);
    } else {
      return this.handleRealResponse('post', this.baseUrl + req.url, req.body);
    }
  }

  mockContractPutResponse(req: HttpRequest<any>) {
    if (environment.name === 'development' || this.storageManagerService.load('token') === 'demotoken') {
      const updatedContract = req.body;
      const contractIndex = exampleGarageContracts.findIndex((contract) => contract.id === req.url.split('/')[6]);
      if (contractIndex === -1) {
        return of(new HttpResponse({ status: 404, body: 'Contract not found' }));
      }

      if (updatedContract.license_plates) {
        updatedContract.license_plates.forEach((updatedLicensePlate: any) => {
          const existingLicensePlate = examplePayPerUseLicensePlates.find(
            (lp) =>
              lp.license_plate === updatedLicensePlate.license_plate &&
              lp.license_plate_country === updatedLicensePlate.license_plate_country,
          );

          if (!existingLicensePlate) {
            examplePayPerUseLicensePlates.push({
              ...updatedLicensePlate,
              pay_per_use_enabled: false,
            });
            exampleLicensePlates.push({
              ...updatedLicensePlate,
            });
          }
        });
      }

      exampleGarageContracts[contractIndex] = {
        ...exampleGarageContracts[contractIndex],
        ...updatedContract,
      };

      return this.handleMockResponse(exampleGarageContracts[contractIndex], req);
    } else {
      return this.handleRealResponse('put', this.baseUrl + req.url, req.body);
    }
  }

  mockContractGetInfoResponse(req: HttpRequest<any>) {
    if (environment.name === 'development' || this.storageManagerService.load('token') === 'demotoken') {
      const url = req.urlWithParams;
      const params = new URLSearchParams(url.split('?')[1]);
      const contract_template_id = params.get('contract_template_id')!;
      const group_limit = params.get('group_limit')!;
      const start = params.get('start')!;

      // Find the corresponding contract template from example data
      const contractTemplate: ContractTemplate = exampleProducts.find((product) => contract_template_id.endsWith(product.id));
      if (!contractTemplate) {
        return this.handleMockError(400);
      }

      let startDate = new Date(start);
      let lastDayOfMonth = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);
      let daysInMonth = lastDayOfMonth.getDate();
      let daysLeftInMonth = daysInMonth - startDate.getDate() + 1;

      // Assuming piece_price is the monthly price
      let piecePrice = contractTemplate.price.amount;
      let price = piecePrice * parseInt(group_limit);
      let firstPaymentPeriodPrice = (price / daysInMonth) * daysLeftInMonth;
      let adjusted_price: number | null = null;
      if (contractTemplate.price_adjustment) {
        if (contractTemplate.price_adjustment?.type === 'percentage') {
          adjusted_price = Math.round(price * (1 + Number(contractTemplate.price_adjustment.amount) / 100) * 100) / 100;
        } else {
          adjusted_price = price + Number(contractTemplate.price_adjustment.amount.amount);
        }
      }

      return this.handleMockResponse(
        {
          name: contractTemplate.name,
          group_limit: 2,
          start: start,
          end: null,
          discount_type: 'P',
          discount_amount: '0.00',
          piece_price: piecePrice.toString(),
          price: price.toString(),
          price_currency: contractTemplate.price.currency,
          vat_percent: '20.00',
          first_termination_date: '2024-12-31',
          first_payment_period: {
            price: firstPaymentPeriodPrice,
            start: '2024-11-12',
            end: '2024-11-30',
          },
          adjusted_price,
        },
        req,
      );
    } else {
      return this.handleRealResponse('get', this.baseUrl + req.url, req.body);
    }
  }

  mockContractTerminateResponse(req: HttpRequest<any>) {
    if (environment.name === 'development' || this.storageManagerService.load('token') === 'demotoken') {
      const updatedContract = req.body;
      const contractIndex = exampleGarageContracts.findIndex((contract) => contract.id === req.url.split('/')[5]);
      if (contractIndex === -1) {
        return of(new HttpResponse({ status: 404, body: 'Contract not found' }));
      } else {
        exampleGarageContracts[contractIndex].end = updatedContract.end;
        return this.handleMockResponse({}, req);
      }
    } else {
      return this.handleRealResponse('put', this.baseUrl + req.url, req.body);
    }
  }

  mockContractWithdrawResponse(req: HttpRequest<any>) {
    if (environment.name === 'development' || this.storageManagerService.load('token') === 'demotoken') {
      const contractIndex = exampleGarageContracts.findIndex((contract) => contract.id === req.url.split('/')[5]);
      if (contractIndex === -1) {
        return of(new HttpResponse({ status: 404, body: 'Contract not found' }));
      } else {
        exampleGarageContracts[contractIndex].end = new Date().toISOString();
        exampleGarageContracts[contractIndex].withdrawable = false;
        return this.handleMockResponse({}, req);
      }
    } else {
      return this.handleRealResponse('put', this.baseUrl + req.url, req.body);
    }
  }

  mockPayPerUseWithdrawResponse(req: HttpRequest<any>) {
    if (environment.name === 'development' || this.storageManagerService.load('token') === 'demotoken') {
      return this.handleMockResponse({}, req);
    } else {
      return this.handleRealResponse('post', this.baseUrl + req.url, req.body);
    }
  }

  mockUserPutResponse(req: HttpRequest<any>) {
    if (environment.name === 'development' || this.storageManagerService.load('token') === 'demotoken') {
      exampleUser.email = req.body.email;
      exampleUser.first_name = req.body.first_name;
      exampleUser.last_name = req.body.last_name;
      exampleUser.street = req.body.street;
      exampleUser.street_number = req.body.street_number;
      exampleUser.zip_code = req.body.zip_code;
      exampleUser.city = req.body.city;
      exampleUser.country = req.body.country;
      exampleUser.business = req.body.business;
      exampleUser.company_name = req.body.company_name;
      exampleUser.vat_id = req.body.vat_id;
      return this.handleMockResponse(null, req);
    } else {
      return this.handleRealResponse('put', this.baseUrl + req.url, req.body);
    }
  }

  mockProductsResponse(req: HttpRequest<any>) {
    if (this.storageManagerService.load('token') === 'demotoken') {
      let garages = exampleGarages;
      addRandomProductsToGarages(garages);
      return this.handleMockResponse(
        {
          map_options: {
            center: null,
            zoom: 14,
          },
          garages,
        },
        req,
      );
    } else {
      return this.handleRealResponse('get', this.baseUrl + req.url);
    }
  }

  mockVehiclesResponse(req: HttpRequest<any>) {
    if (environment.name === 'development' || this.storageManagerService.load('token') === 'demotoken') {
      const vehicles = examplePayPerUseLicensePlates.map((plate: PayPerUseLicensePlate) => {
        const vehicle: Vehicle = {
          id: Math.random().toString(36).substring(2, 15),
          license_plate_country: plate.license_plate_country,
          license_plate: plate.license_plate,
          description: '',
          pay_per_use_enabled: plate.pay_per_use_enabled,
          garage_contracts: [],
        };

        exampleGarageContracts.forEach((contract) => {
          if (
            contract.license_plates.some(
              (lp: LicensePlate) => lp.license_plate === plate.license_plate && lp.license_plate_country === plate.license_plate_country,
            )
          ) {
            vehicle.garage_contracts.push({
              id: contract.id,
              name: contract.name,
              group_limit: contract.group_limit,
              garage: contract.garage
                ? {
                  name: contract.garage.name,
                }
                : null,
            });
          }
        });
        return vehicle;
      });
      exampleVehicles.length = 0;
      exampleVehicles.push(...vehicles);

      return this.handleMockResponse(vehicles, req);
    } else {
      return this.handleRealResponse('get', this.baseUrl + req.url);
    }
  }

  mockVehicleDeleteResponse(req: HttpRequest<any>) {
    if (environment.name === 'development' || this.storageManagerService.load('token') === 'demotoken') {
      const vehicleId = req.url.split('/')[6];
      const vehicle = exampleVehicles.find((vehicle) => vehicle.id === vehicleId);
      if (vehicle) {
        const { license_plate, license_plate_country } = vehicle;

        // Remove from examplePayPerUseLicensePlates
        const ppuIndex = examplePayPerUseLicensePlates.findIndex(
          (plate) => plate.license_plate === license_plate && plate.license_plate_country === license_plate_country,
        );
        if (ppuIndex !== -1) {
          examplePayPerUseLicensePlates.splice(ppuIndex, 1);
        }
        const plateIndex = exampleLicensePlates.findIndex(
          (plate) => plate.license_plate === license_plate && plate.license_plate_country === license_plate_country,
        );
        if (plateIndex !== -1) {
          exampleLicensePlates.splice(plateIndex, 1);
        }

        // Remove from exampleVehicles
        const vehicleIndex = exampleVehicles.indexOf(vehicle);
        exampleVehicles.splice(vehicleIndex, 1);
      }

      return this.handleMockResponse(null, req);
    } else {
      return this.handleRealResponse('delete', this.baseUrl + req.url);
    }
  }

  mockVehicleDescriptionPutResponse(req: HttpRequest<any>) {
    if (environment.name === 'development' || this.storageManagerService.load('token') === 'demotoken') {
      const vehicleId = req.url.split('/')[6];
      const description = req.body?.description ?? '';
      const vehicle = exampleVehicles.find((vehicle) => vehicle.id === vehicleId);

      if (!vehicle) {
        return this.handleMockError(404);
      }

      vehicle.description = description;
      return this.handleMockResponse({ id: vehicleId, description }, req);
    } else {
      return this.handleRealResponse('put', this.baseUrl + req.url, req.body);
    }
  }

  mockRegisterEmailResponse(req: HttpRequest<any>) {
    if (environment.name === 'development') {
      return this.handleMockResponse(null, req);
    } else {
      return this.handleRealResponse('post', this.baseUrl + req.url, req.body);
    }
  }

  mockLoginEmailResponse(req: HttpRequest<any>) {
    return this.mockRegisterEmailResponse(req);
  }

  mockWelcomeEmailFromTokenResponse(req: HttpRequest<any>) {
    if (environment.name === 'development') {
      if (req.body.token === "666666666") {
        return this.handleMockResponse({}, req);
      } else {
        return this.handleMockError(401, { error_code: 'invalid_token' });
      }
    } else {
      return this.handleRealResponse('post', this.baseUrl + req.url, req.body);
    }
  }

  mockEmailVerifyResponse(req: HttpRequest<any>) {
    if (environment.name === 'development') {
      if (req.body.key === 'correct:key') {
        return this.handleMockResponse({}, req);
      } else if (req.body.key === 'correct:401:key') {
        let emailVerifyLoginResponse = this.loginResponse();
        return this.handleMockError(401, emailVerifyLoginResponse);
      } else {
        return this.handleMockError(410, {})
      }
    } else {
      return this.handleRealResponse('post', this.baseUrl + req.url, req.body);
    }
  }

  mockLoginTokenResponse(req: HttpRequest<any>) {
    if (environment.name === 'development' || this.storageManagerService.load('token') === 'demotoken') {
      if (req.body.code === '000000') {
        return this.handleMockResponse(
          {
            data: {
              methods: [],
              user: {
                id: 'userid',
                display: 'userdisplay',
                has_usable_password: false,
                email: 'demouser@demo.at',
                username: 'demousername',
              },
            },
            meta: {
              is_authenticated: true,
              session_token: 'demotoken',
            },
          },
          req,
        );
      } else if (req.body.code === 'DEVICE') {
        return this.handleMockError(400, { error_code: 'incorrect_code' });
      } else if (req.body.code === "GOOGLE") {
        let googleLoginResponse = this.loginResponse();
        googleLoginResponse.data.flows.push({"id": "verify_email", "is_pending": true});
        return this.handleMockError(401, googleLoginResponse);
      }
      else {
        return this.handleMockError(401);
      }
    } else {
      return this.handleRealResponse('post', this.baseUrl + req.url, req.body);
    }
  }

  mockConfirmLoginCookieResponse(req: HttpRequest<any>) {
    if (environment.name === 'development' || this.storageManagerService.load('token') === 'demotoken') {
      let googleLoginResponse = this.loginResponse();
      googleLoginResponse.data.flows.push({"id": "verify_email", "is_pending": true});
      return this.handleMockError(401, googleLoginResponse);
    } else {
      return this.handleRealResponse('post', this.baseUrl + req.url, req.body);
    }
  }

  mockActivateAccountResponse(req: HttpRequest<any>) {
    if (environment.name === 'development') {
      return this.handleMockResponse(
        {
          data: {
            methods: [],
            user: {
              id: 'userid',
              display: 'userdisplay',
              has_usable_password: false,
              email: 'demouser@demo.at',
              username: 'demousername',
            },
          },
          meta: {
            is_authenticated: true,
            session_token: 'demotoken',
          },
          status: 200,
        },
        req,
      );
    } else {
      return this.handleRealResponse('post', this.baseUrl + req.url, req.body);
    }
  }

  mockLogoutResponse(req: HttpRequest<any>) {
    if (environment.name === 'development' || this.storageManagerService.load('token') === 'demotoken') {
      return this.handleMockResponse({}, req);
    } else {
      return this.handleRealResponse('delete', this.baseUrl + req.url, req.body);
    }
  }

  private mockContractEditResponse(req: HttpRequest<any>) {
    if (environment.name === 'development' || this.storageManagerService.load('token') === 'demotoken') {
      const contract = exampleGarageContracts.find((contract) => contract.id === req.url.split('/')[6]);
      if (!contract) {
        console.warn(`Contract ${req.url.split('/')[6]} not found!`);
        return this.handleMockError(404);
      }
      return this.handleMockResponse({ garage_contract: contract, license_plates: exampleLicensePlates }, req);
    } else {
      return this.handleRealResponse('get', this.baseUrl + req.url);
    }
  }

  private handleMockResponse<T>(responseBody: T, req: HttpRequest<any>, delayMs: number = 500): Observable<HttpResponse<T>> {
    console.warn('Mocking API call:', req.method, req.url);
    return of(new HttpResponse({ status: 200, body: responseBody })).pipe(delay(delayMs));
  }

  private handleMockError<T>(status: number, body?: T, delayMs: number = 500): Observable<never> {
    console.warn('Mocking API error response:', status);

    return of(null).pipe(
      delay(delayMs),
      switchMap(() =>
        throwError(
          () =>
            new HttpErrorResponse({
              status,
              statusText: 'Mock error',
              error: body ?? null,
              url: 'mock://local',
            }),
        ),
      ),
    );
  }

  private handleRealResponse<T>(method: string, url: string, body?: any, options?: any): Observable<HttpResponse<T>> {
    console.warn('Mock overwritten by real API call:', method, url);
    let request$: Observable<any>;

    switch (method.toLowerCase()) {
      case 'get':
        request$ = this.http.get<T>(url, { observe: 'response', ...options });
        break;
      case 'post':
        request$ = this.http.post<T>(url, body, { observe: 'response', ...options });
        break;
      case 'put':
        request$ = this.http.put<T>(url, body, { observe: 'response', ...options });
        break;
      case 'delete':
        request$ = this.http.delete<T>(url, { observe: 'response', ...options });
        break;
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }

    return request$.pipe(switchMap((response) => of(new HttpResponse({ status: 200, body: response.body }))));
  }
}
